import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';

const GALLERY_IMAGES = [
  'https://novagob.org/wp-content/uploads/2025/12/DSC00788-1536x1025.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC00768-1536x1025.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01111-1536x1025.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01164-1536x1026.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01221-1536x1026.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01376-1536x1026.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01101-1536x1026.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01082-1536x1010.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01092-1536x1026.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01070-1536x1020.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01063-1536x964.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC00997-1536x1025.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC00965-1536x1026.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01014-1536x1062.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01009-1536x1028.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC00752-1536x1025.jpg',
  'https://novagob.org/wp-content/uploads/2025/12/DSC01129-1536x891.jpg',
];

const YOUTUBE_ID = '2NNgFulmgz4';
const AUTOPLAY_MS = 6000;
const TOTAL = GALLERY_IMAGES.length;

const ReviveSection: React.FC = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const goPrev = useCallback(
    () => setIndex((i) => (i - 1 + TOTAL) % TOTAL),
    []
  );
  const goNext = useCallback(() => setIndex((i) => (i + 1) % TOTAL), []);

  useEffect(() => {
    if (paused || lightboxOpen) return;
    const t = setInterval(goNext, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, goNext, lightboxOpen]);

  // Controles de teclado cuando el lightbox está abierto
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [lightboxOpen, goPrev, goNext]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  return (
    <section id="revive" className="landing-section landing-revive">
      <div className="landing-container">
        <h2 className="landing-section-title text-gold-shine">
          Revive la edición 2025
        </h2>
        <p className="landing-section-lead">
          Una selección de momentos de la Gala de los Premios NovaGob
          Excelencia 2025, celebrada en Madrid. Pulsa sobre cualquier foto
          para verla a pantalla completa.
        </p>

        <div
          className="revive-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="region"
          aria-roledescription="carousel"
          aria-label="Galería de imágenes de los Premios NovaGob 2025"
        >
          <div className="revive-carousel-track" data-index={index}>
            {GALLERY_IMAGES.map((src, i) => {
              // distancia relativa al slide activo (con wrap-around)
              let offset = i - index;
              if (offset > TOTAL / 2) offset -= TOTAL;
              if (offset < -TOTAL / 2) offset += TOTAL;
              const isActive = offset === 0;
              return (
                <button
                  key={src}
                  type="button"
                  className={`revive-carousel-slide${
                    isActive ? ' active' : ''
                  }`}
                  onClick={() => {
                    if (!isActive) setIndex(i);
                    else setLightboxOpen(true);
                  }}
                  style={{
                    transform: `translateX(calc(${offset} * (100% + 20px)))`,
                  }}
                  aria-hidden={!isActive}
                  aria-label={
                    isActive
                      ? `Abrir foto ${i + 1} a pantalla completa`
                      : `Ir a la foto ${i + 1}`
                  }
                  tabIndex={isActive ? 0 : -1}
                >
                  <img
                    src={src}
                    alt={`Gala Premios NovaGob 2025 — foto ${i + 1}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    draggable={false}
                  />
                  {isActive && (
                    <span className="revive-carousel-zoom" aria-hidden="true">
                      <Maximize2 size={16} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="revive-carousel-btn revive-carousel-btn-prev"
            onClick={goPrev}
            aria-label="Foto anterior"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            type="button"
            className="revive-carousel-btn revive-carousel-btn-next"
            onClick={goNext}
            aria-label="Foto siguiente"
          >
            <ChevronRight size={24} />
          </button>

          <div className="revive-carousel-counter" aria-hidden="true">
            {String(index + 1).padStart(2, '0')} /{' '}
            {String(TOTAL).padStart(2, '0')}
          </div>
        </div>

        <div
          className="revive-carousel-dots"
          role="tablist"
          aria-label="Seleccionar foto"
        >
          {GALLERY_IMAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`revive-dot${i === index ? ' active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`Ir a la foto ${i + 1}`}
              aria-selected={i === index}
              role="tab"
            />
          ))}
        </div>

        <div className="revive-video">
          <h3 className="revive-video-title">Lo mejor de la Gala 2025</h3>
          <div className="revive-video-frame">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_ID}?rel=0&modestbranding=1`}
              title="Premios NovaGob 2025 — Lo mejor de la gran noche de la innovación pública"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </div>

      {/* Lightbox fullscreen */}
      {lightboxOpen && (
        <div
          className="revive-lightbox"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Visor de foto ampliada"
        >
          <button
            type="button"
            className="revive-lightbox-close"
            aria-label="Cerrar"
            onClick={() => setLightboxOpen(false)}
          >
            <X size={22} />
          </button>

          <button
            type="button"
            className="revive-lightbox-nav revive-lightbox-nav-prev"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Foto anterior"
          >
            <ChevronLeft size={28} />
          </button>

          <img
            src={GALLERY_IMAGES[index]}
            alt={`Gala Premios NovaGob 2025 — foto ${index + 1}`}
            className="revive-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            type="button"
            className="revive-lightbox-nav revive-lightbox-nav-next"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Foto siguiente"
          >
            <ChevronRight size={28} />
          </button>

          <div className="revive-lightbox-counter" aria-live="polite">
            {index + 1} / {TOTAL}
          </div>
        </div>
      )}
    </section>
  );
};

export default ReviveSection;
